import { Topbar, Aside, Main, Loader } from 'components';
import { Link } from 'react-router-dom';
import { useGetUser } from 'hooks';
// `dateToHumanFormat` is a helper, not data — stays local.
import { dateToHumanFormat } from 'components/misc/constants';
import useConfigOptions from 'hooks/useConfigOptions';
import { HugeiconsIcon } from '@hugeicons/react';
import { CheckmarkCircle02Icon, Edit01Icon, AttachmentIcon, Alert01Icon } from '@hugeicons/core-free-icons';
import { FileItem } from 'components';
import { useUser } from 'hooks';

function Field({ label, value, mono }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-start py-3.5 border-b border-gray-100 dark:border-white/[.06] last:border-0">
            <dt className="w-full sm:w-1/3 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 pt-0.5 shrink-0">
                {label}
            </dt>
            <dd className={`mt-1 sm:mt-0 text-sm text-gray-800 dark:text-gray-200 ${mono ? 'font-mono' : ''}`}>
                {value || <span className="text-gray-300 dark:text-gray-600">—</span>}
            </dd>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="rounded-xl border border-gray-200 dark:border-white/[.08] bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-100 dark:border-white/[.06]">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</h3>
            </div>
            <dl className="px-5">
                {children}
            </dl>
        </div>
    );
}

function Index() {
    const { employmentNatureOptions, occupationOptions } = useConfigOptions();
    const { profile, loading } = useGetUser();
    const { accessToken, user } = useUser();

    if (loading) return <Loader />;

    return (
        <main>
            <Topbar />

            <div className='pt-14 w-full flex overflow-hidden bg-light-bg dark:bg-dark-page'>
                <Aside />

                <Main>
                    <div className="space-y-5 py-4">

                        {/* Page header */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Profile</h1>
                                {profile?.isVerified ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-400 ring-1 ring-inset ring-emerald-600/20 dark:ring-emerald-500/20">
                                        <HugeiconsIcon icon={CheckmarkCircle02Icon} size={13} />
                                        Verified
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-400 ring-1 ring-inset ring-amber-600/20 dark:ring-amber-500/20">
                                        <HugeiconsIcon icon={Alert01Icon} size={13} />
                                        {profile?.verificationStatus === 'pending' ? 'Pending review' : 'Unverified'}
                                    </span>
                                )}
                            </div>

                            <Link
                                to="/profile/edit"
                                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
                            >
                                <HugeiconsIcon icon={Edit01Icon} size={14} className="text-gray-400" />
                                Edit
                            </Link>
                        </div>

                        {/* Unverified alert */}
                        {!profile?.isVerified && (
                            <div className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30 px-4 py-3.5 flex items-start gap-3">
                                <HugeiconsIcon icon={Alert01Icon} size={18} className="text-amber-500 shrink-0 mt-0.5" />
                                <p className="text-sm text-amber-700 dark:text-amber-400">
                                    {profile?.verificationStatus === 'pending' ? (
                                        <>Your <strong className="font-semibold">GhanaCard</strong> details are being reviewed by our team. Loan applications open once your account is verified.</>
                                    ) : (
                                        <>
                                            {profile?.verificationStatus === 'rejected' && <>We could not verify the GhanaCard details you submitted. </>}
                                            Complete your <strong className="font-semibold">Next of Kin</strong> and <strong className="font-semibold">GhanaCard</strong> details to verify your account and unlock loan applications.{' '}
                                            <Link to="/profile/edit" className="underline font-semibold hover:text-amber-900 dark:hover:text-amber-300">Complete profile →</Link>
                                        </>
                                    )}
                                </p>
                            </div>
                        )}

                        {/* Personal */}
                        <Section title="Personal Information">
                            <Field label="First Name" value={profile?.otherNames} />
                            <Field label="Last Name" value={profile?.surname} />
                            <Field label="Gender" value={profile?.gender} />
                            <Field label="Date of Birth" value={dateToHumanFormat(profile?.dob)} />
                            <Field label="Nationality" value={profile?.nationality} />
                            <Field label="Place of Birth" value={profile?.placeOfBirth} />
                        </Section>

                        {/* Contact */}
                        <Section title="Contact">
                            <Field label="Phone / Network" value={profile?.phone ? `${profile.phone} / ${profile.network}` : null} />
                            <Field label="Email Address" value={profile?.email} />
                        </Section>

                        {/* Address */}
                        <Section title="Address">
                            <Field label="Residential Address" value={profile?.address} />
                            <Field label="Digital Address" value={profile?.digitalAddress} mono />
                            <Field label="Nearest Landmark" value={profile?.nearestLandmark} />
                        </Section>

                        {/* Employment */}
                        <Section title="Employment">
                            <Field label="Education Level" value={profile?.education} />
                            <Field label="Occupation" value={occupationOptions.find(o => o.value === profile?.occupation)?.text} />
                            <Field label="Employment Nature" value={employmentNatureOptions.find(o => o.value === profile?.employNature)?.text} />
                        </Section>

                        {/* Identity & Next of Kin */}
                        <Section title="Identity & Next of Kin">
                            <Field label="ID Type / Number" value={profile?.idType && profile?.idNumber ? `${profile.idType} / ${profile.idNumber}` : null} mono />
                            <Field label="Next of Kin Name" value={profile?.nextOfKin} />
                            <Field label="Next of Kin Phone" value={profile?.nextOfKinPhone} />
                            <Field label="Next of Kin Address" value={profile?.nextOfKinAddress} />
                            <Field label="Relationship" value={profile?.nextOfKinRelationship} />
                        </Section>

                        {/* Uploaded files */}
                        <div className="rounded-xl border border-gray-200 dark:border-white/[.08] bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
                            <div className="px-5 py-3.5 border-b border-gray-100 dark:border-white/[.06]">
                                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Uploaded Files & IDs</h3>
                            </div>
                            <ul className="divide-y divide-gray-100 dark:divide-white/[.06]">
                                {profile?.uploadedFiles && profile.uploadedFiles.length > 0 ? (
                                    profile.uploadedFiles.map((file, index) => (
                                        <FileItem
                                            key={index}
                                            file={file}
                                            phone={user?.phone}
                                            accessToken={accessToken}
                                        />
                                    ))
                                ) : (
                                    <li className="flex items-center gap-2 px-5 py-4 text-sm text-gray-400 dark:text-gray-500">
                                        <HugeiconsIcon icon={AttachmentIcon} size={16} className="text-gray-300 dark:text-gray-600" />
                                        No files uploaded yet
                                    </li>
                                )}
                            </ul>
                        </div>

                    </div>
                </Main>
            </div>
        </main>
    );
}

export default Index;
